import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import OrderNumbersInput, { MAX_ORDER_NUMBERS, parseOrderNumbers } from "./components/OrderNumbersInput"
import ResultsTable from "./components/ResultsTable"
import { fetchNfeStatus } from "../../../services/naturaNfeStatusService"

const STATUS_IDLE = "idle"
const STATUS_LOADING = "loading"
const STATUS_SUCCESS = "success"
const STATUS_ERROR_INLINE = "error_inline"   // 422 validation error with safe message
const STATUS_ERROR_GENERIC = "error_generic" // 5xx / network / timeout / other 4xx

export default function NotaFiscalPage() {
  const { t } = useTranslation()
  const [raw, setRaw] = useState("")
  const [status, setStatus] = useState(STATUS_IDLE)
  const [response, setResponse] = useState(null)
  const [inlineError, setInlineError] = useState(null)
  const [submittedOrder, setSubmittedOrder] = useState([])

  const parsed = useMemo(() => parseOrderNumbers(raw), [raw])
  const tooMany = parsed.length > MAX_ORDER_NUMBERS
  const isLoading = status === STATUS_LOADING

  const submit = async () => {
    if (parsed.length === 0 || tooMany || isLoading) return
    setStatus(STATUS_LOADING)
    setResponse(null)
    setInlineError(null)
    setSubmittedOrder(parsed)
    try {
      const data = await fetchNfeStatus(parsed)
      setResponse(data)
      setStatus(STATUS_SUCCESS)
    } catch (e) {
      const httpStatus = e?.response?.status
      // Only 422 carries a user-safe validation message from the platform.
      // Other 4xx (401/403/etc.) should fall through to the generic block to
      // avoid leaking internal error strings.
      if (httpStatus === 422) {
        const payload = e?.response?.data
        setInlineError(payload?.message || payload?.error || t("natura_operations.nfe_status.error_generic"))
        setStatus(STATUS_ERROR_INLINE)
      } else {
        setStatus(STATUS_ERROR_GENERIC)
      }
    }
  }

  const retry = () => {
    submit()
  }

  const submitDisabled = isLoading || parsed.length === 0 || tooMany

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">{t("natura_operations.nfe_status.title")}</h1>

      <section className="bg-gray-50 border border-gray-200 rounded p-4 mb-6">
        <h2 className="font-semibold mb-2">{t("natura_operations.nfe_status.instructions_heading")}</h2>
        <ol className="list-decimal pl-6 text-sm space-y-1">
          <li>{t("natura_operations.nfe_status.instructions_step1")}</li>
          <li>{t("natura_operations.nfe_status.instructions_step2")}</li>
          <li>{t("natura_operations.nfe_status.instructions_step3")}</li>
        </ol>
        <p className="mt-3 text-sm text-amber-700">{t("natura_operations.nfe_status.warning_wait")}</p>
      </section>

      {status === STATUS_ERROR_INLINE && inlineError && (
        <div role="alert" className="mb-4 p-3 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          {inlineError}
        </div>
      )}

      <OrderNumbersInput
        value={raw}
        onChange={setRaw}
        parsed={parsed}
        disabled={isLoading}
      />

      <div className="mt-3">
        <button
          type="button"
          onClick={submit}
          disabled={submitDisabled}
          className="px-4 py-2 rounded bg-amber-500 hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed text-white"
        >
          {isLoading
            ? t("natura_operations.nfe_status.submit_loading", { count: submittedOrder.length || parsed.length })
            : t("natura_operations.nfe_status.submit")}
        </button>
      </div>

      {status === STATUS_ERROR_GENERIC && (
        <div className="mt-6 p-4 border border-red-200 bg-red-50 rounded text-sm text-red-800">
          <p className="mb-2">{t("natura_operations.nfe_status.error_generic")}</p>
          <button
            type="button"
            onClick={retry}
            className="px-3 py-1.5 text-sm rounded bg-red-600 hover:bg-red-700 text-white"
          >
            {t("natura_operations.nfe_status.retry")}
          </button>
        </div>
      )}

      {status === STATUS_SUCCESS && response && (
        <ResultsTable
          summary={response.summary}
          results={response.results || []}
          inputOrder={submittedOrder}
        />
      )}
    </div>
  )
}
